import { useState, useEffect, useCallback, Fragment, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Package, Search, RefreshCw, AlertTriangle, Calendar, TrendingUp, 
  Loader2, X, Check, Clipboard, Eye, DollarSign, ArrowDownCircle, Trash2, ChevronDown, ChevronUp, Edit2,
  Camera, User, FileText, Plus, Image as ImageIcon, Percent, Filter, Clock, History, Pencil
} from "lucide-react";
import { STORE_LOCATIONS } from "@/constants";
import { useStock } from "@/hooks/useStock";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL as __BACKEND_URL__ } from "../lib/backend-url";
import { StockDualValue } from "@/components/Stock/StockDualValue";

const API = __BACKEND_URL__ + "/api";

const WRITE_OFF_REASONS = [
  { value: "danificado", label: "Produto Danificado", color: "bg-red-100 text-red-700" },
  { value: "degustacao", label: "Degustação", color: "bg-blue-100 text-blue-700" },
  { value: "consumo_equipe", label: "Consumo Equipe", color: "bg-amber-100 text-amber-700" },
  { value: "beneficio_colaborador", label: "Benefício Colaborador", color: "bg-emerald-100 text-emerald-700" },
  { value: "vencido", label: "Produto Vencido", color: "bg-purple-100 text-purple-700" },
  { value: "outros", label: "Outros", color: "bg-slate-100 text-slate-700" }
];
export function StockPage({ 
  onOpenPriceModal
}) {
  const {
    stockProducts,
    loadingStock,
    lowStockProducts,
    stockProductDetail,
    loadingStockDetail,
    deletingMovement,
    loadStockProducts,
    loadLowStockProducts,
    loadStockProductDetail,
    updateMinStock: updateMinStockApi,
    updateProductImage,
    updateBatchExpiry,
    processSalesForStock,
    deleteMovement,
    clearStockProductDetail,
    invalidateCache
  } = useStock();

  const [stockSearch, setStockSearch] = useState("");
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [stockStoreId, setStockStoreId] = useState("cangaiba"); // Default to Vencidinho Cangaíba
  const [expandedProduct, setExpandedProduct] = useState(null); // EAN of expanded product
  const [editingMinStock, setEditingMinStock] = useState(null);
  const [newMinStock, setNewMinStock] = useState("");
  const [editingProductImage, setEditingProductImage] = useState(null);
  const [editingDetailImage, setEditingDetailImage] = useState(false); // For image in detail view
  const [editingBatchExpiry, setEditingBatchExpiry] = useState(null); // batch_id being edited
  const [newBatchExpiry, setNewBatchExpiry] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingBatch, setDeletingBatch] = useState(null); // batch_id being deleted
  const [confirmDeleteBatch, setConfirmDeleteBatch] = useState(null); // batch to confirm deletion
  
  // Column filters
  const [filterProduct, setFilterProduct] = useState("");
  const [filterEan, setFilterEan] = useState("");
  const [filterStockMin, setFilterStockMin] = useState("");
  const [filterStockMax, setFilterStockMax] = useState("");
  const [filterMinStockMin, setFilterMinStockMin] = useState("");
  const [filterMinStockMax, setFilterMinStockMax] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all" | "low" | "ok" | "unavailable"
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  
  // Write-off (Baixa) states
  const [activeTab, setActiveTab] = useState("stock"); // "stock" | "writeoff"
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [writeOffEan, setWriteOffEan] = useState("");
  const [writeOffQty, setWriteOffQty] = useState("");
  const [writeOffReason, setWriteOffReason] = useState("");
  const [writeOffNotes, setWriteOffNotes] = useState("");
  const [writeOffCollaborator, setWriteOffCollaborator] = useState("");
  const [writeOffPhoto, setWriteOffPhoto] = useState(null); // Base64 photo
  const [writeOffProductName, setWriteOffProductName] = useState(""); // For uncatalogued products
  const [submittingWriteOff, setSubmittingWriteOff] = useState(false);
  const [writeOffs, setWriteOffs] = useState([]);
  const [loadingWriteOffs, setLoadingWriteOffs] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [showAddCollaborator, setShowAddCollaborator] = useState(false);
  const [newCollaboratorName, setNewCollaboratorName] = useState("");
  const [writeOffProduct, setWriteOffProduct] = useState(null); // Selected product info
  const [searchingProduct, setSearchingProduct] = useState(false);
  const writeOffSearchAbortRef = useRef(null);
  const writeOffSearchTimeoutRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Sales rhythm state
  const [salesRhythm, setSalesRhythm] = useState(null);
  const [loadingSalesRhythm, setLoadingSalesRhythm] = useState(false);
  
  // Markup state
  const [productMarkup, setProductMarkup] = useState(null);
  const [loadingMarkup, setLoadingMarkup] = useState(false);
  
  // Price history state
  const [priceHistory, setPriceHistory] = useState(null);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);
  const [priceHistoryPeriod, setPriceHistoryPeriod] = useState("all"); // all, week, month
  
  // Expiring products alert state
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [loadingExpiring, setLoadingExpiring] = useState(false);
  const [showAllExpiringModal, setShowAllExpiringModal] = useState(false);
  
  // Expiry filter states
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [expiryDaysThreshold, setExpiryDaysThreshold] = useState(7); // Days to consider "próximo do vencimento"
  
  // Purchase status tracking (em cotação, em compra)
  const [purchaseStatus, setPurchaseStatus] = useState({}); // { [ean]: { inQuote: boolean, inPurchase: boolean } }

  // Product name editing
  const [editingProductName, setEditingProductName] = useState(null); // EAN of product being edited
  const [newProductName, setNewProductName] = useState("");
  const [nameChangeReason, setNameChangeReason] = useState("");
  const [savingProductName, setSavingProductName] = useState(false);
  const [productNameHistory, setProductNameHistory] = useState([]);
  const [loadingNameHistory, setLoadingNameHistory] = useState(false);

  // Load purchase status from localStorage on mount
  useEffect(() => {
    const savedStatus = localStorage.getItem('productPurchaseStatus');
    if (savedStatus) {
      try {
        setPurchaseStatus(JSON.parse(savedStatus));
      } catch (e) {
        console.error("Error loading purchase status:", e);
      }
    }
  }, []);

  // Save purchase status to localStorage when it changes
  useEffect(() => {
    if (Object.keys(purchaseStatus).length > 0) {
      localStorage.setItem('productPurchaseStatus', JSON.stringify(purchaseStatus));
    }
  }, [purchaseStatus]);

  // Toggle purchase status for a product
  const togglePurchaseStatus = (ean, field) => {
    setPurchaseStatus(prev => ({
      ...prev,
      [ean]: {
        ...prev[ean],
        [field]: !(prev[ean]?.[field] || false)
      }
    }));
  };

  // Load expiring products (including expired)
  const loadExpiringProducts = useCallback(async () => {
    setLoadingExpiring(true);
    try {
      const response = await axios.get(`${API}/stock/expiring`, {
        params: { 
          store_id: stockStoreId || undefined, 
          days: expiryDaysThreshold,
          include_expired: true  // Include expired products
        }
      });
      if (response.data.success) {
        setExpiringProducts(response.data.products || []);
      }
    } catch (error) {
      console.error("Error loading expiring products:", error);
    } finally {
      setLoadingExpiring(false);
    }
  }, [stockStoreId, expiryDaysThreshold]);

  // Load data on mount and when store changes
  useEffect(() => {
    loadStockProducts(stockStoreId);
    loadLowStockProducts(stockStoreId);
    loadExpiringProducts();
  }, [stockStoreId, loadStockProducts, loadLowStockProducts, loadExpiringProducts]);

  // Reload product detail when store changes and a product is expanded
  useEffect(() => {
    if (expandedProduct) {
      loadStockProductDetail(expandedProduct, stockStoreId);
    }
  }, [stockStoreId, expandedProduct, loadStockProductDetail]);

  // Load sales analysis when product is expanded
  const loadSalesRhythm = useCallback(async (ean) => {
    setLoadingSalesRhythm(true);
    setSalesRhythm(null);
    try {
      const response = await axios.get(`${API}/stock/products/${ean}/sales-analysis`, {
        params: { store_id: stockStoreId || undefined }
      });
      if (response.data.success) {
        setSalesRhythm(response.data);
      }
    } catch (error) {
      console.error("Error loading sales analysis:", error);
    } finally {
      setLoadingSalesRhythm(false);
    }
  }, [stockStoreId]);
  
  // Load markup when product is expanded
  const loadProductMarkup = useCallback(async (ean) => {
    setLoadingMarkup(true);
    setProductMarkup(null);
    try {
      const response = await axios.get(`${API}/sales/product-markup/${ean}`, {
        params: { store_id: stockStoreId || undefined }
      });
      if (response.data.success) {
        setProductMarkup(response.data);
      }
    } catch (error) {
      console.error("Error loading product markup:", error);
    } finally {
      setLoadingMarkup(false);
    }
  }, [stockStoreId]);

  // Load price history for a product
  const loadPriceHistory = useCallback(async (ean, period = "all") => {
    setLoadingPriceHistory(true);
    try {
      const response = await axios.get(`${API}/sales/product-prices/${ean}`, {
        params: { store_id: stockStoreId || undefined, period }
      });
      if (response.data.success) {
        setPriceHistory(response.data);
      }
    } catch (error) {
      console.error("Error loading price history:", error);
    } finally {
      setLoadingPriceHistory(false);
    }
  }, [stockStoreId]);

  // Load sales analysis and markup when product is expanded or store changes
  useEffect(() => {
    if (expandedProduct) {
      loadSalesRhythm(expandedProduct);
      loadProductMarkup(expandedProduct);
      loadPriceHistory(expandedProduct, priceHistoryPeriod);
    } else {
      setSalesRhythm(null);
      setProductMarkup(null);
      setPriceHistory(null);
    }
  }, [expandedProduct, stockStoreId, loadSalesRhythm, loadProductMarkup, loadPriceHistory, priceHistoryPeriod]);

  // Load write-offs and collaborators
  const loadWriteOffs = useCallback(async () => {
    setLoadingWriteOffs(true);
    try {
      const response = await axios.get(`${API}/stock/write-offs`, {
        params: { store_id: stockStoreId || undefined, limit: 50 }
      });
      if (response.data.success) {
        setWriteOffs(response.data.write_offs || []);
      }
    } catch (error) {
      console.error("Error loading write-offs:", error);
    } finally {
      setLoadingWriteOffs(false);
    }
  }, [stockStoreId]);

  const loadCollaborators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stock/collaborators`);
      if (response.data.success) {
        setCollaborators(response.data.collaborators || []);
      }
    } catch (error) {
      console.error("Error loading collaborators:", error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "writeoff") {
      loadWriteOffs();
      loadCollaborators();
    }
  }, [activeTab, loadWriteOffs, loadCollaborators]);

  // Handle photo capture/upload
  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setWriteOffPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Search product for write-off (debounced + cancelable)
  const searchProductForWriteOff = (ean) => {
    // Always clear any pending debounced search and in-flight request
    if (writeOffSearchTimeoutRef.current) {
      clearTimeout(writeOffSearchTimeoutRef.current);
      writeOffSearchTimeoutRef.current = null;
    }
    if (writeOffSearchAbortRef.current) {
      writeOffSearchAbortRef.current.abort();
      writeOffSearchAbortRef.current = null;
    }

    if (!ean || ean.length < 8) {
      setWriteOffProduct(null);
      setSearchingProduct(false);
      return;
    }

    setSearchingProduct(true);
    writeOffSearchTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      writeOffSearchAbortRef.current = controller;
      try {
        const response = await axios.get(`${API}/stock/products/${ean}`, {
          signal: controller.signal,
          timeout: 15000,
        });
        // Only apply if this is still the active request
        if (writeOffSearchAbortRef.current !== controller) return;
        if (response.data.success && response.data.product) {
          setWriteOffProduct(response.data.product);
        } else {
          setWriteOffProduct(null);
        }
      } catch (error) {
        if (axios.isCancel(error) || error.name === "CanceledError" || error.name === "AbortError") {
          return; // Ignore canceled
        }
        setWriteOffProduct(null);
      } finally {
        if (writeOffSearchAbortRef.current === controller) {
          setSearchingProduct(false);
          writeOffSearchAbortRef.current = null;
        }
      }
    }, 350);
  };

  // Submit write-off
  const submitWriteOff = async () => {
    if (!writeOffEan || !writeOffQty || !writeOffReason || !writeOffCollaborator) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    
    if (!stockStoreId) {
      toast.error("Selecione uma loja");
      return;
    }

    setSubmittingWriteOff(true);
    try {
      const response = await axios.post(`${API}/stock/write-off`, {
        ean: writeOffEan,
        quantity: parseFloat(writeOffQty),
        store_id: stockStoreId,
        reason: writeOffReason,
        collaborator_name: writeOffCollaborator,
        product_name: writeOffProductName || null,
        notes: writeOffNotes || null,
        photo_base64: writeOffPhoto || null
      });

      if (response.data.success) {
        toast.success(response.data.message || "Baixa registrada com sucesso!");
        // Reset form
        setWriteOffEan("");
        setWriteOffQty("");
        setWriteOffReason("");
        setWriteOffNotes("");
        setWriteOffPhoto(null);
        setWriteOffProduct(null);
        setWriteOffProductName("");
        setShowWriteOffModal(false);
        // Reload data
        loadWriteOffs();
        loadStockProducts(stockStoreId);
        loadLowStockProducts(stockStoreId);
      } else {
        toast.error(response.data.error || "Erro ao registrar baixa");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar baixa");
    } finally {
      setSubmittingWriteOff(false);
    }
  };

  // Add collaborator
  const addCollaborator = async () => {
    if (!newCollaboratorName.trim()) return;
    try {
      const response = await axios.post(`${API}/stock/collaborators`, {
        name: newCollaboratorName.trim()
      });
      if (response.data.success) {
        setCollaborators(prev => [...prev, response.data.collaborator]);
        setWriteOffCollaborator(newCollaboratorName.trim());
        setNewCollaboratorName("");
        setShowAddCollaborator(false);
        toast.success("Colaborador adicionado");
      }
    } catch (error) {
      toast.error("Erro ao adicionar colaborador");
    }
  };

  // Delete write-off
  const deleteWriteOff = async (writeOffId) => {
    if (!window.confirm("Excluir esta baixa e restaurar o estoque?")) return;
    try {
      const response = await axios.delete(`${API}/stock/write-off/${writeOffId}`);
      if (response.data.success) {
        toast.success(response.data.message);
        loadWriteOffs();
        loadStockProducts(stockStoreId);
      }
    } catch (error) {
      toast.error("Erro ao excluir baixa");
    }
  };

  // ==================== PRODUCT NAME EDITING ====================
  
  const startEditingProductName = async (ean, currentName) => {
    setEditingProductName(ean);
    setNewProductName(currentName || "");
    setNameChangeReason("");
    // Load name history
    loadProductNameHistory(ean);
  };

  const loadProductNameHistory = async (ean) => {
    setLoadingNameHistory(true);
    try {
      const response = await axios.get(`${API}/stock/products/${ean}/name-history`);
      if (response.data.success) {
        setProductNameHistory(response.data.history || []);
      }
    } catch (error) {
      console.error("Error loading name history:", error);
      setProductNameHistory([]);
    } finally {
      setLoadingNameHistory(false);
    }
  };

  const saveProductName = async () => {
    if (!editingProductName || !newProductName.trim()) {
      toast.error("Nome não pode ser vazio");
      return;
    }
    
    setSavingProductName(true);
    try {
      const response = await axios.put(`${API}/stock/products/${editingProductName}/name`, {
        new_name: newProductName.trim(),
        reason: nameChangeReason || null
      });
      
      if (response.data.success) {
        toast.success("Nome atualizado com sucesso!");
        setEditingProductName(null);
        setNewProductName("");
        setNameChangeReason("");
        // Reload stock to show updated name
        loadStockProducts(stockStoreId);
        // If detail is open, reload it
        if (stockProductDetail?.product?.ean === editingProductName) {
          loadStockProductDetail(editingProductName, stockStoreId);
        }
      } else {
        toast.error(response.data.message || "Erro ao atualizar nome");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar nome");
    } finally {
      setSavingProductName(false);
    }
  };

  const cancelEditingProductName = () => {
    setEditingProductName(null);
    setNewProductName("");
    setNameChangeReason("");
    setProductNameHistory([]);
  };

  // Filter products by search
  const filteredProducts = stockProducts.filter(p => {
    const matchesSearch = !stockSearch || 
      p.name?.toLowerCase().includes(stockSearch.toLowerCase()) ||
      p.ean?.includes(stockSearch);
    const matchesLowStock = !showLowStockOnly || p.is_low_stock;
    
    // Column filters
    const matchesProductFilter = !filterProduct || 
      p.name?.toLowerCase().includes(filterProduct.toLowerCase());
    const matchesEanFilter = !filterEan || 
      p.ean?.includes(filterEan);
    
    // Stock filter (only for products with NF-e entry)
    const matchesStockFilter = 
      (!filterStockMin && !filterStockMax) || 
      (p.has_nfe_entry && 
        (!filterStockMin || p.current_stock >= parseFloat(filterStockMin)) &&
        (!filterStockMax || p.current_stock <= parseFloat(filterStockMax)));
    
    // Min stock filter
    const matchesMinStockFilter = 
      (!filterMinStockMin && !filterMinStockMax) || 
      ((!filterMinStockMin || (p.min_stock || 0) >= parseFloat(filterMinStockMin)) &&
       (!filterMinStockMax || (p.min_stock || 0) <= parseFloat(filterMinStockMax)));
    
    // Status filter
    const matchesStatusFilter = 
      filterStatus === "all" ||
      (filterStatus === "low" && p.is_low_stock && p.has_nfe_entry) ||
      (filterStatus === "ok" && !p.is_low_stock && p.has_nfe_entry) ||
      (filterStatus === "unavailable" && !p.has_nfe_entry);
    
    // Expiry filter - check if product is in expiringProducts list
    const expiringProduct = expiringProducts.find(ep => ep.ean === p.ean);
    const isExpired = expiringProduct?.is_expired === true;
    const isExpiring = expiringProduct && !isExpired && expiringProduct.days_until_expiry <= expiryDaysThreshold;
    
    const matchesExpiryFilter = 
      (!showExpiringOnly && !showExpiredOnly) ||
      (showExpiredOnly && isExpired) ||
      (showExpiringOnly && isExpiring);
    
    return matchesSearch && matchesLowStock && matchesProductFilter && 
           matchesEanFilter && matchesStockFilter && matchesMinStockFilter && matchesStatusFilter && matchesExpiryFilter;
  });
  
  // Calculate expiry counts for display
  const expiredCount = expiringProducts.filter(p => p.is_expired).length;
  const expiringCount = expiringProducts.filter(p => !p.is_expired && p.days_until_expiry <= expiryDaysThreshold).length;
  
  // Categorize expiring products by write-off status
  const categorizedExpiringProducts = {
    // Vencidos SEM baixa
    expiredNoWriteOff: expiringProducts.filter(p => {
      if (!p.is_expired) return false;
      const hasWriteOff = writeOffs.some(w => 
        w.ean === p.ean && 
        w.reason === "vencimento" &&
        new Date(w.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // Last 30 days
      );
      return !hasWriteOff;
    }),
    // Vencidos COM baixa
    expiredWithWriteOff: expiringProducts.filter(p => {
      if (!p.is_expired) return false;
      const hasWriteOff = writeOffs.some(w => 
        w.ean === p.ean && 
        w.reason === "vencimento" &&
        new Date(w.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // Last 30 days
      );
      return hasWriteOff;
    }),
    // À vencer (próximos dias)
    expiringSoon: expiringProducts.filter(p => !p.is_expired && p.days_until_expiry <= expiryDaysThreshold)
  };

  // Quick write-off function for expired products
  const handleQuickWriteOff = async (product) => {
    if (!window.confirm(`Registrar baixa por vencimento de "${product.name || product.product_name}" (${product.remaining_qty || product.total_stock || 0} ${product.unit || 'UN'})?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/stock/write-off`, {
        ean: product.ean,
        quantity: product.remaining_qty || product.total_stock || 1,
        store_id: stockStoreId || "vencidinho_cangaiba",
        reason: "vencimento",
        collaborator_name: "Sistema (Baixa Rápida)",
        product_name: product.name || product.product_name,
        notes: `Baixa automática - Vencido em ${product.expiry_date}`
      });
      
      if (response.data.success) {
        toast.success(`Baixa registrada para ${product.name || product.ean}`);
        loadWriteOffs();
        loadExpiringProducts();
        loadStockProducts(stockStoreId);
      } else {
        toast.error(response.data.error || "Erro ao registrar baixa");
      }
    } catch (error) {
      console.error("Quick write-off error:", error);
      toast.error(error.response?.data?.detail || "Erro ao registrar baixa");
    }
  };

  const handleToggleExpand = useCallback((ean) => {
    if (expandedProduct === ean) {
      setExpandedProduct(null);
      clearStockProductDetail();
    } else {
      setExpandedProduct(ean);
      loadStockProductDetail(ean, stockStoreId);
    }
  }, [expandedProduct, stockStoreId, loadStockProductDetail, clearStockProductDetail]);

  const handleUpdateMinStock = useCallback(async (ean, value) => {
    const success = await updateMinStockApi(ean, value);
    if (success) {
      setEditingMinStock(null);
      loadStockProducts(stockStoreId);
      loadLowStockProducts(stockStoreId);
    }
  }, [updateMinStockApi, stockStoreId, loadStockProducts, loadLowStockProducts]);

  const handlePasteProductImage = useCallback(async (e, ean) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const base64 = event.target?.result;
            const success = await updateProductImage(ean, base64);
            if (success) {
              setEditingProductImage(null);
              loadStockProducts(stockStoreId);
              if (expandedProduct === ean) {
                loadStockProductDetail(ean, stockStoreId);
              }
            }
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, [updateProductImage, stockStoreId, expandedProduct, loadStockProducts, loadStockProductDetail]);

  const handlePasteDetailImage = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items || !expandedProduct) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const base64 = event.target?.result;
            const success = await updateProductImage(expandedProduct, base64);
            if (success) {
              setEditingDetailImage(false);
              loadStockProducts(stockStoreId);
              loadStockProductDetail(expandedProduct, stockStoreId);
            }
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, [updateProductImage, expandedProduct, stockStoreId, loadStockProducts, loadStockProductDetail]);

  const handleUpdateBatchExpiry = useCallback(async (batchId, newExpiry) => {
    const success = await updateBatchExpiry(batchId, newExpiry);
    if (success) {
      setEditingBatchExpiry(null);
      if (expandedProduct) {
        loadStockProductDetail(expandedProduct, stockStoreId);
      }
    }
  }, [updateBatchExpiry, expandedProduct, stockStoreId, loadStockProductDetail]);

  const handleDeleteBatch = useCallback(async (batch) => {
    setDeletingBatch(batch.id);
    try {
      const response = await axios.delete(
        `${API}/stock/batches/${batch.id}?reason=Exclusão manual&collaborator=Usuário`
      );
      if (response.data.success) {
        toast.success(`Lote ${batch.batch_number} excluído com sucesso`);
        setConfirmDeleteBatch(null);
        if (expandedProduct) {
          loadStockProductDetail(expandedProduct, stockStoreId);
        }
        loadStockProducts(stockStoreId);
        loadLowStockProducts(stockStoreId);
      }
    } catch (error) {
      console.error("Error deleting batch:", error);
      toast.error("Erro ao excluir lote: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingBatch(null);
    }
  }, [expandedProduct, stockStoreId, loadStockProductDetail, loadStockProducts, loadLowStockProducts]);

  const handleProcessSales = useCallback(async () => {
    const success = await processSalesForStock();
    if (success) {
      loadStockProducts(stockStoreId);
      loadLowStockProducts(stockStoreId);
    }
  }, [processSalesForStock, stockStoreId, loadStockProducts, loadLowStockProducts]);

  const handleRefresh = useCallback(async () => {
    // Invalidate cache and reload with fresh data
    await invalidateCache();
    loadStockProducts(stockStoreId, false); // false = don't use cache
    loadLowStockProducts(stockStoreId);
  }, [stockStoreId, loadStockProducts, loadLowStockProducts, invalidateCache]);

  const handleDeleteMovement = useCallback(async (movementId) => {
    const result = await deleteMovement(movementId);
    if (result) {
      setConfirmDelete(null);
      if (expandedProduct) {
        loadStockProductDetail(expandedProduct, stockStoreId);
      }
      loadStockProducts(stockStoreId);
      loadLowStockProducts(stockStoreId);
    }
  }, [deleteMovement, expandedProduct, stockStoreId, loadStockProductDetail, loadStockProducts, loadLowStockProducts]);

  // Build product object for external modals
  const buildProductObject = (product) => ({
    ean: product.ean,
    product_name: product.name,
    image_url: product.image_url,
    image_base64: product.image_base64,
    cost_price: product.cost_price
  });

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Expiring Products Alert - Categorized by write-off status */}
      {expiringProducts.length > 0 && (
        <Card className="shadow-sm border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="py-4 px-4">
            <div className="flex flex-col gap-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">
                      Controle de Vencimentos
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 text-sm mt-1">
                      {categorizedExpiringProducts.expiredNoWriteOff.length > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                          🔴 {categorizedExpiringProducts.expiredNoWriteOff.length} vencido{categorizedExpiringProducts.expiredNoWriteOff.length > 1 ? 's' : ''} sem baixa
                        </Badge>
                      )}
                      {categorizedExpiringProducts.expiredWithWriteOff.length > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          ✅ {categorizedExpiringProducts.expiredWithWriteOff.length} vencido{categorizedExpiringProducts.expiredWithWriteOff.length > 1 ? 's' : ''} com baixa
                        </Badge>
                      )}
                      {categorizedExpiringProducts.expiringSoon.length > 0 && (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                          ⚠️ {categorizedExpiringProducts.expiringSoon.length} à vencer
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {loadingExpiring && <Loader2 className="w-5 h-5 animate-spin text-amber-600" />}
              </div>
              
              {/* Section 1: Vencidos SEM baixa (urgent - needs action) */}
              {categorizedExpiringProducts.expiredNoWriteOff.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <h5 className="text-sm font-semibold text-red-800">Vencidos - Aguardando Baixa ({categorizedExpiringProducts.expiredNoWriteOff.length})</h5>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categorizedExpiringProducts.expiredNoWriteOff.slice(0, 4).map((p, idx) => (
                      <div 
                        key={`expired-no-wo-${p.ean}-${idx}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 border border-red-300"
                      >
                        {(p.image_base64 || p.image_url) ? (
                          <img src={p.image_base64 || p.image_url} alt="" className="w-10 h-10 object-contain rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-red-50 rounded flex items-center justify-center">
                            <Package className="w-5 h-5 text-red-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate max-w-[120px]">{p.name || p.product_name}</p>
                          <p className="text-xs text-red-600 font-medium">
                            Venceu há {Math.abs(p.days_until_expiry)}d • {p.remaining_qty || p.total_stock || 0} {p.unit || 'UN'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleQuickWriteOff(p); }}
                          className="ml-2 bg-red-600 hover:bg-red-700 text-white h-7 px-2 text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Baixa
                        </Button>
                      </div>
                    ))}
                    {categorizedExpiringProducts.expiredNoWriteOff.length > 4 && (
                      <button 
                        onClick={() => setShowAllExpiringModal(true)}
                        className="flex items-center px-3 py-2 text-sm text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-300 transition-colors font-medium"
                      >
                        +{categorizedExpiringProducts.expiredNoWriteOff.length - 4} mais...
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Section 2: Vencidos COM baixa (resolved) */}
              {categorizedExpiringProducts.expiredWithWriteOff.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <h5 className="text-sm font-semibold text-emerald-800">Vencidos - Baixa Registrada ({categorizedExpiringProducts.expiredWithWriteOff.length})</h5>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categorizedExpiringProducts.expiredWithWriteOff.slice(0, 3).map((p, idx) => (
                      <div 
                        key={`expired-with-wo-${p.ean}-${idx}`}
                        onClick={() => handleToggleExpand(p.ean)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 cursor-pointer hover:bg-emerald-100"
                      >
                        {(p.image_base64 || p.image_url) ? (
                          <img src={p.image_base64 || p.image_url} alt="" className="w-8 h-8 object-contain rounded opacity-60" />
                        ) : (
                          <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                            <Package className="w-4 h-4 text-emerald-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-600 truncate max-w-[120px] line-through">{p.name || p.product_name}</p>
                          <p className="text-xs text-emerald-600">
                            ✓ Baixa registrada
                          </p>
                        </div>
                      </div>
                    ))}
                    {categorizedExpiringProducts.expiredWithWriteOff.length > 3 && (
                      <button 
                        onClick={() => setShowAllExpiringModal(true)}
                        className="flex items-center px-3 py-2 text-xs text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
                      >
                        +{categorizedExpiringProducts.expiredWithWriteOff.length - 3} mais
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Section 3: À Vencer (upcoming) */}
              {categorizedExpiringProducts.expiringSoon.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <h5 className="text-sm font-semibold text-orange-800">À Vencer - Próximos {expiryDaysThreshold} dias ({categorizedExpiringProducts.expiringSoon.length})</h5>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categorizedExpiringProducts.expiringSoon.slice(0, 4).map((p, idx) => (
                      <div 
                        key={`expiring-${p.ean}-${idx}`}
                        className={`flex flex-col gap-2 px-3 py-2 rounded-lg transition-all ${
                          p.days_until_expiry <= 2 
                            ? 'bg-orange-100 border border-orange-300'
                            : 'bg-amber-50 border border-amber-200'
                        }`}
                      >
                        <div 
                          onClick={() => handleToggleExpand(p.ean)}
                          className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                        >
                          {(p.image_base64 || p.image_url) ? (
                            <img src={p.image_base64 || p.image_url} alt="" className="w-10 h-10 object-contain rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-amber-100 rounded flex items-center justify-center">
                              <Package className="w-5 h-5 text-amber-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate max-w-[120px]">{p.name || p.product_name}</p>
                            <p className={`text-xs font-medium ${p.days_until_expiry <= 2 ? 'text-orange-600' : 'text-amber-600'}`}>
                              {p.days_until_expiry === 0 ? 'Vence HOJE!' : p.days_until_expiry === 1 ? 'Vence amanhã' : `Vence em ${p.days_until_expiry}d`}
                            </p>
                            <p className="text-xs text-slate-500">
                              {p.remaining_qty || p.total_stock || 0} {p.unit || 'UN'}
                              {p.sales_last_7d !== undefined && <> • {p.sales_last_7d}/7d</>}
                            </p>
                          </div>
                        </div>
                        {/* Purchase Status Checkboxes */}
                        <div className="flex flex-col gap-1 pt-1 border-t border-amber-200">
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                            <input 
                              type="checkbox" 
                              checked={purchaseStatus[p.ean]?.inQuote || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                togglePurchaseStatus(p.ean, 'inQuote');
                              }}
                              className="w-3.5 h-3.5 rounded border-amber-400 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={purchaseStatus[p.ean]?.inQuote ? 'text-blue-700 font-medium' : 'text-slate-600'}>
                              Em cotação
                            </span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                            <input 
                              type="checkbox" 
                              checked={purchaseStatus[p.ean]?.inPurchase || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                togglePurchaseStatus(p.ean, 'inPurchase');
                              }}
                              className="w-3.5 h-3.5 rounded border-amber-400 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className={purchaseStatus[p.ean]?.inPurchase ? 'text-emerald-700 font-medium' : 'text-slate-600'}>
                              Em compra
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                    {categorizedExpiringProducts.expiringSoon.length > 4 && (
                      <button 
                        onClick={() => setShowAllExpiringModal(true)}
                        className="flex items-center px-3 py-2 text-sm text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors font-medium"
                      >
                        +{categorizedExpiringProducts.expiringSoon.length - 4} mais...
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal to show all expiring products */}
      <Dialog open={showAllExpiringModal} onOpenChange={setShowAllExpiringModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              Todos os Produtos com Vencimento Próximo ({expiringProducts.length})
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {/* Summary */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-red-100 text-red-700 border-red-200">
                {expiringProducts.filter(p => p.is_expired).length} Vencidos
              </Badge>
              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                {expiringProducts.filter(p => !p.is_expired && p.days_until_expiry <= 2).length} Vence em até 2 dias
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                {expiringProducts.filter(p => !p.is_expired && p.days_until_expiry > 2).length} Vence em 3-7 dias
              </Badge>
            </div>
            
            {/* Products List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {expiringProducts.map((p, idx) => (
                <div 
                  key={`${p.ean}-${idx}`}
                  className={`flex flex-col gap-2 p-3 rounded-lg border transition-all ${
                    p.is_expired 
                      ? 'bg-red-50 border-red-300' 
                      : p.days_until_expiry <= 2 
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div 
                    onClick={() => {
                      setShowAllExpiringModal(false);
                      handleToggleExpand(p.ean);
                    }}
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80"
                  >
                    {(p.image_base64 || p.image_url) ? (
                      <img src={p.image_base64 || p.image_url} alt="" className="w-12 h-12 object-contain rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                        <Package className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{p.name || p.product_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{p.ean}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          p.is_expired 
                            ? 'bg-red-200 text-red-800' 
                            : p.days_until_expiry <= 2 
                              ? 'bg-orange-200 text-orange-800'
                              : 'bg-amber-200 text-amber-800'
                        }`}>
                          {p.is_expired 
                            ? `Vencido há ${Math.abs(p.days_until_expiry)}d`
                            : p.days_until_expiry === 0 
                              ? 'HOJE!'
                              : `${p.days_until_expiry}d`
                          }
                        </span>
                        <span className="text-xs text-slate-500">
                          Val: {p.expiry_date}
                        </span>
                        <span className="text-xs text-emerald-600 font-medium">
                          {p.remaining_qty || p.total_stock || 0} {p.unit || 'UN'}
                        </span>
                      </div>
                    </div>
                    {/* Quick write-off button for expired products without write-off */}
                    {p.is_expired && !writeOffs.some(w => w.ean === p.ean && w.reason === "vencimento" && new Date(w.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) && (
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleQuickWriteOff(p); }}
                        className="ml-2 bg-red-600 hover:bg-red-700 text-white h-8 px-3 text-xs shrink-0"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Baixa
                      </Button>
                    )}
                    {/* Show check if already has write-off */}
                    {p.is_expired && writeOffs.some(w => w.ean === p.ean && w.reason === "vencimento" && new Date(w.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) && (
                      <div className="ml-2 px-2 py-1 bg-emerald-100 rounded text-emerald-700 text-xs flex items-center shrink-0">
                        <Check className="w-3 h-3 mr-1" />
                        Baixa OK
                      </div>
                    )}
                  </div>
                  {/* Purchase Status Checkboxes - Only for non-expired products */}
                  {!p.is_expired && (
                    <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                        <input 
                          type="checkbox" 
                          checked={purchaseStatus[p.ean]?.inQuote || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            togglePurchaseStatus(p.ean, 'inQuote');
                          }}
                          className="w-3.5 h-3.5 rounded border-amber-400 text-blue-600 focus:ring-blue-500"
                        />
                        <span className={purchaseStatus[p.ean]?.inQuote ? 'text-blue-700 font-medium' : 'text-slate-600'}>
                          Em cotação
                        </span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                        <input 
                          type="checkbox" 
                          checked={purchaseStatus[p.ean]?.inPurchase || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            togglePurchaseStatus(p.ean, 'inPurchase');
                          }}
                          className="w-3.5 h-3.5 rounded border-amber-400 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className={purchaseStatus[p.ean]?.inPurchase ? 'text-emerald-700 font-medium' : 'text-slate-600'}>
                          Em compra
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("stock")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "stock"
              ? 'border-amber-600 text-amber-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Package className="w-4 h-4 mr-2 inline" />
          Estoque
        </button>
        <button
          onClick={() => setActiveTab("writeoff")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "writeoff"
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Trash2 className="w-4 h-4 mr-2 inline" />
          Baixas
        </button>
      </div>

      {/* ==================== STOCK TAB ==================== */}
      {activeTab === "stock" && (
        <>
          {/* Search and Filters */}
          <Card className="shadow-sm border-slate-200">
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              <div className="flex flex-col gap-3 md:gap-4">
                <div className="flex flex-col sm:flex-row gap-2 md:gap-4 items-stretch sm:items-center">
                  {/* Store Selector */}
                  <select
                    value={stockStoreId}
                    onChange={(e) => setStockStoreId(e.target.value)}
                    className="h-10 px-3 border border-slate-200 rounded-md text-sm bg-white w-full sm:min-w-[180px] sm:w-auto"
                  >
                    <option value="">Todas as Lojas</option>
                    {STORE_LOCATIONS.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por EAN ou nome..."
                      value={stockSearch}
                      onChange={(e) => setStockSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showLowStockOnly}
                        onChange={(e) => setShowLowStockOnly(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-600">
                        Estoque baixo
                        {lowStockProducts.length > 0 && (
                          <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-200 text-xs">
                            {lowStockProducts.length}
                          </Badge>
                        )}
                      </span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showExpiringOnly}
                        onChange={(e) => {
                          setShowExpiringOnly(e.target.checked);
                          if (e.target.checked) setShowExpiredOnly(false);
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-slate-600">
                        Próx. vencimento
                        {expiringCount > 0 && (
                          <Badge variant="outline" className="ml-1 bg-orange-50 text-orange-700 border-orange-200 text-xs">
                            {expiringCount}
                          </Badge>
                        )}
                      </span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showExpiredOnly}
                        onChange={(e) => {
                          setShowExpiredOnly(e.target.checked);
                          if (e.target.checked) setShowExpiringOnly(false);
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">
                        Vencidos
                        {expiredCount > 0 && (
                          <Badge variant="outline" className="ml-1 bg-red-50 text-red-700 border-red-200 text-xs">
                            {expiredCount}
                          </Badge>
                        )}
                      </span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleRefresh} variant="outline" className="border-slate-200 flex-1 sm:flex-none" size="sm">
                      <RefreshCw className="w-4 h-4 mr-1 md:mr-2" />
                      <span className="hidden sm:inline">Atualizar</span>
                    </Button>
                    <Button onClick={handleProcessSales} className="bg-amber-600 hover:bg-amber-700 text-white flex-1 sm:flex-none" size="sm">
                      <ArrowDownCircle className="w-4 h-4 mr-1 md:mr-2" />
                      <span className="hidden sm:inline">Processar Vendas</span>
                      <span className="sm:hidden">Processar</span>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Low Stock Alert */}
          {lowStockProducts.length > 0 && (
            <Card className="shadow-sm border-red-200 bg-red-50">
              <CardHeader className="pb-2 px-3 md:px-6">
                <CardTitle className="text-base md:text-lg flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Alerta de Estoque Baixo ({lowStockProducts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 md:px-6">
                <div className="flex flex-wrap gap-2">
              {lowStockProducts.slice(0, 10).map((p) => (
                <div
                  key={p.ean}
                  onClick={() => handleToggleExpand(p.ean)}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-red-200 cursor-pointer hover:shadow-md transition-all"
                >
                  {(p.image_base64 || p.image_url) && (
                    <img src={p.image_base64 || p.image_url} alt="" className="w-8 h-8 object-contain rounded" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-800 truncate max-w-[150px]">{p.name}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <StockDualValue product={p} size="sm" inline />
                      <span className="text-slate-400">mín: {p.min_stock}</span>
                    </div>
                  </div>
                </div>
              ))}
              {lowStockProducts.length > 10 && (
                <div className="flex items-center px-3 py-2 text-sm text-red-600">
                  +{lowStockProducts.length - 10} mais...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Table */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            Produtos em Estoque ({filteredProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingStock ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Carregando estoque...</span>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Nenhum produto no estoque</p>
              <p className="text-sm text-slate-400 mt-1">Importe notas fiscais para cadastrar produtos</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700 w-8">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => setShowColumnFilters(!showColumnFilters)}
                        title={showColumnFilters ? "Ocultar filtros" : "Mostrar filtros"}
                      >
                        <Filter className={`w-3 h-3 ${showColumnFilters ? 'text-blue-600' : 'text-slate-400'}`} />
                      </Button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700">Produto</TableHead>
                    <TableHead className="font-semibold text-slate-700">EAN</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Estoque</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Mínimo</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Destaque</TableHead>
                  </TableRow>
                  {showColumnFilters && (
                    <TableRow className="bg-slate-100 border-t border-slate-200">
                      <TableHead className="py-2"></TableHead>
                      <TableHead className="py-2">
                        <Input
                          placeholder="Filtrar produto..."
                          value={filterProduct}
                          onChange={(e) => setFilterProduct(e.target.value)}
                          className="h-7 text-xs"
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <Input
                          placeholder="Filtrar EAN..."
                          value={filterEan}
                          onChange={(e) => setFilterEan(e.target.value)}
                          className="h-7 text-xs font-mono"
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <div className="flex gap-1">
                          <Input
                            type="number"
                            placeholder="Mín"
                            value={filterStockMin}
                            onChange={(e) => setFilterStockMin(e.target.value)}
                            className="h-7 text-xs w-16"
                          />
                          <Input
                            type="number"
                            placeholder="Máx"
                            value={filterStockMax}
                            onChange={(e) => setFilterStockMax(e.target.value)}
                            className="h-7 text-xs w-16"
                          />
                        </div>
                      </TableHead>
                      <TableHead className="py-2">
                        <div className="flex gap-1">
                          <Input
                            type="number"
                            placeholder="Mín"
                            value={filterMinStockMin}
                            onChange={(e) => setFilterMinStockMin(e.target.value)}
                            className="h-7 text-xs w-16"
                          />
                          <Input
                            type="number"
                            placeholder="Máx"
                            value={filterMinStockMax}
                            onChange={(e) => setFilterMinStockMax(e.target.value)}
                            className="h-7 text-xs w-16"
                          />
                        </div>
                      </TableHead>
                      <TableHead className="py-2">
                        <select
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          className="h-7 text-xs border rounded px-2 w-full bg-white"
                        >
                          <option value="all">Todos</option>
                          <option value="ok">OK</option>
                          <option value="low">Baixo</option>
                          <option value="unavailable">Indisponível</option>
                        </select>
                      </TableHead>
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <Fragment key={product.ean}>
                      {/* Main Product Row */}
                      <TableRow 
                        className={`cursor-pointer transition-colors ${
                          product.is_low_stock ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'
                        } ${expandedProduct === product.ean ? 'bg-blue-50' : ''}`}
                        onClick={() => handleToggleExpand(product.ean)}
                      >
                        <TableCell className="w-8">
                          {expandedProduct === product.ean ? (
                            <ChevronUp className="w-4 h-4 text-blue-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {editingProductImage === product.ean ? (
                              <div 
                                className="w-10 h-10 bg-amber-100 rounded border-2 border-dashed border-amber-400 flex items-center justify-center cursor-pointer"
                                tabIndex={0}
                                onPaste={(e) => { e.stopPropagation(); handlePasteProductImage(e, product.ean); }}
                                onBlur={() => setEditingProductImage(null)}
                                onClick={(e) => e.stopPropagation()}
                                title="Cole uma imagem (Ctrl+V)"
                              >
                                <Clipboard className="w-4 h-4 text-amber-600" />
                              </div>
                            ) : (product.image_base64 || product.image_url) ? (
                              <img 
                                src={product.image_base64 || product.image_url} 
                                alt="" 
                                className="w-10 h-10 object-contain rounded border border-slate-200 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all" 
                                onClick={(e) => { e.stopPropagation(); setEditingProductImage(product.ean); }}
                                title="Clique para editar foto"
                              />
                            ) : (
                              <div 
                                className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center cursor-pointer hover:bg-amber-100 hover:border-amber-400 border border-transparent transition-all"
                                onClick={(e) => { e.stopPropagation(); setEditingProductImage(product.ean); }}
                                title="Clique para adicionar foto"
                              >
                                <Package className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <span className="font-medium text-slate-800 truncate max-w-[250px]">{product.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600 font-mono text-sm">{product.ean}</TableCell>
                        <TableCell className="text-center">
                          {product.has_nfe_entry ? (
                            <div className="flex justify-center">
                              <StockDualValue product={product} size="md" />
                            </div>
                          ) : product.pg_saldo !== null && product.pg_saldo !== undefined ? (
                            <div className="flex justify-center">
                              <StockDualValue product={product} size="md" />
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400 italic">Indisponível</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {editingMinStock === product.ean ? (
                            <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Input
                                type="number"
                                value={newMinStock}
                                onChange={(e) => setNewMinStock(e.target.value)}
                                className="w-16 h-7 text-sm text-center"
                              />
                              <Button size="sm" className="h-7 px-2" onClick={() => handleUpdateMinStock(product.ean, newMinStock)}>
                                <Check className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span 
                              className="text-slate-600 cursor-pointer hover:text-blue-600"
                              onClick={(e) => { e.stopPropagation(); setEditingMinStock(product.ean); setNewMinStock(product.min_stock); }}
                            >
                              {product.min_stock}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {!product.has_nfe_entry ? (
                            <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Sem NF-e
                            </Badge>
                          ) : product.is_low_stock ? (
                            <Badge variant="destructive" className="text-xs">Baixo</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            checked={product.is_highlighted || false}
                            onChange={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.put(`${API}/stock/products/${product.ean}`, {
                                  is_highlighted: e.target.checked
                                });
                                loadStockProducts();
                                toast.success(e.target.checked ? "Produto marcado como destaque" : "Destaque removido");
                              } catch (err) {
                                toast.error("Erro ao atualizar destaque");
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
                            title={product.is_highlighted ? "Produto em destaque na loja" : "Marcar como destaque"}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded Detail Row */}
                      {expandedProduct === product.ean && (
                        <TableRow className="bg-blue-50/50">
                          <TableCell colSpan={7} className="p-0">
                            <div className="p-4 border-t border-blue-200">
                              {loadingStockDetail ? (
                                <div className="flex items-center gap-2 text-blue-600 py-4 justify-center">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Carregando detalhes...</span>
                                </div>
                              ) : stockProductDetail ? (
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                                  {/* Product Image */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100 flex flex-col items-center justify-center">
                                    {editingDetailImage ? (
                                      <div 
                                        className="w-32 h-32 bg-amber-100 rounded-xl border-2 border-dashed border-amber-400 flex flex-col items-center justify-center cursor-pointer"
                                        tabIndex={0}
                                        onPaste={handlePasteDetailImage}
                                        onBlur={() => setEditingDetailImage(false)}
                                        autoFocus
                                      >
                                        <Clipboard className="w-8 h-8 text-amber-600" />
                                        <span className="text-xs text-amber-600 mt-2 text-center">Cole uma imagem<br/>(Ctrl+V)</span>
                                      </div>
                                    ) : (stockProductDetail.product?.image_base64 || stockProductDetail.product?.image_url) ? (
                                      <img 
                                        src={stockProductDetail.product.image_base64 || stockProductDetail.product.image_url} 
                                        alt={stockProductDetail.product.name}
                                        className="w-32 h-32 object-contain rounded-xl border border-slate-200 cursor-pointer hover:border-blue-400 hover:shadow-lg transition-all"
                                        onClick={() => setEditingDetailImage(true)}
                                        title="Clique para trocar a imagem"
                                      />
                                    ) : (
                                      <div 
                                        className="w-32 h-32 bg-slate-100 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-amber-100 transition-all"
                                        onClick={() => setEditingDetailImage(true)}
                                        title="Clique para adicionar imagem"
                                      >
                                        <Package className="w-12 h-12 text-slate-400" />
                                        <span className="text-xs text-slate-400 mt-2">Sem imagem</span>
                                      </div>
                                    )}
                                    <p className="text-xs text-slate-500 mt-2 text-center">Clique para editar</p>
                                  </div>

                                  {/* Product Info & Actions */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <Package className="w-4 h-4" />
                                      Informações
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      {/* Product Name with Edit */}
                                      <div className="flex items-start gap-2">
                                        <span className="text-slate-500 shrink-0">Nome:</span>
                                        {editingProductName === stockProductDetail.product?.ean ? (
                                          <div className="flex-1 space-y-2">
                                            <Input
                                              value={newProductName}
                                              onChange={(e) => setNewProductName(e.target.value)}
                                              className="h-8 text-sm"
                                              placeholder="Nome do produto"
                                            />
                                            <Input
                                              value={nameChangeReason}
                                              onChange={(e) => setNameChangeReason(e.target.value)}
                                              className="h-7 text-xs"
                                              placeholder="Motivo da alteração (opcional)"
                                            />
                                            <div className="flex gap-1">
                                              <Button size="sm" onClick={saveProductName} disabled={savingProductName} className="h-7 text-xs">
                                                {savingProductName ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                              </Button>
                                              <Button size="sm" variant="ghost" onClick={cancelEditingProductName} className="h-7 text-xs">
                                                <X className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2 flex-1">
                                            <span className="font-medium">{stockProductDetail.product?.name}</span>
                                            <Button 
                                              size="sm" 
                                              variant="ghost" 
                                              className="h-6 w-6 p-0"
                                              onClick={() => startEditingProductName(stockProductDetail.product?.ean, stockProductDetail.product?.name)}
                                            >
                                              <Pencil className="w-3 h-3 text-slate-400 hover:text-blue-500" />
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      <p><span className="text-slate-500">EAN:</span> <span className="font-mono">{stockProductDetail.product?.ean}</span></p>
                                      <div className="flex items-start gap-2 my-1">
                                        <span className="text-slate-500 mt-0.5">Estoque:</span>
                                        {stockProductDetail.has_nfe_entry || (stockProductDetail.product?.pg_saldo !== null && stockProductDetail.product?.pg_saldo !== undefined) ? (
                                          <StockDualValue product={stockProductDetail.product} size="sm" showLabel />
                                        ) : (
                                          <span className="text-slate-400 italic">Indisponível</span>
                                        )}
                                      </div>
                                      <p><span className="text-slate-500">Mínimo:</span> {stockProductDetail.product?.min_stock} un</p>
                                      {!stockProductDetail.has_nfe_entry && (
                                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg mt-2">
                                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                                          Sem entrada de NF-e
                                        </p>
                                      )}
                                    </div>
                                    {/* Action Buttons */}
                                    <div className="mt-4 flex flex-wrap gap-2">
                                      <Button 
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); onOpenPriceModal && onOpenPriceModal(buildProductObject(stockProductDetail.product)); }}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                      >
                                        <DollarSign className="w-3 h-3 mr-1" />Preço
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Markup Card */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <Percent className="w-4 h-4 text-emerald-600" />
                                      Markup
                                    </h5>
                                    {loadingMarkup ? (
                                      <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                      </div>
                                    ) : productMarkup ? (
                                      <div className="space-y-3">
                                        {productMarkup.has_cost_data ? (
                                          <>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                              <div className="p-2 rounded-lg bg-red-50">
                                                <p className="text-slate-500">Custo</p>
                                                <p className="font-bold text-red-700">
                                                  R$ {productMarkup.cost_price?.toFixed(2)}
                                                </p>
                                                {productMarkup.cost_date && (
                                                  <p className="text-[10px] text-slate-400">
                                                    NF {productMarkup.cost_date.slice(0, 10)}
                                                  </p>
                                                )}
                                              </div>
                                              <div className="p-2 rounded-lg bg-emerald-50">
                                                <p className="text-slate-500">Venda Média</p>
                                                <p className="font-bold text-emerald-700">
                                                  R$ {productMarkup.avg_sale_price?.toFixed(2)}
                                                </p>
                                                <p className="text-[10px] text-slate-400">
                                                  {productMarkup.total_sales_count} vendas
                                                </p>
                                              </div>
                                            </div>
                                            <div className="p-3 rounded-lg bg-gradient-to-r from-amber-50 to-emerald-50 border border-amber-200">
                                              <div className="flex items-center justify-between">
                                                <span className="text-sm text-slate-600">Markup</span>
                                                <span className={`text-xl font-bold ${
                                                  productMarkup.markup_percent >= 30 
                                                    ? 'text-emerald-600' 
                                                    : productMarkup.markup_percent >= 15 
                                                      ? 'text-amber-600' 
                                                      : 'text-red-600'
                                                }`}>
                                                  {productMarkup.markup_percent?.toFixed(1)}%
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                                                <span>Lucro unitário</span>
                                                <span className="font-medium text-emerald-600">
                                                  R$ {productMarkup.lucro_unitario?.toFixed(2)}
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                                                <span>Fator</span>
                                                <span className="font-medium">
                                                  {productMarkup.markup_fator?.toFixed(2)}x
                                                </span>
                                              </div>
                                            </div>
                                          </>
                                        ) : (
                                          <div className="p-3 rounded-lg bg-slate-50 text-center">
                                            <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                                            <p className="text-xs text-slate-500">
                                              Sem dados de custo
                                            </p>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                              Importe uma NF-e com este produto
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-slate-400 text-center py-2">Sem dados</p>
                                    )}
                                  </div>

                                  {/* Stock Availability Card */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <Package className="w-4 h-4 text-blue-600" />
                                      Estoque Disponível
                                    </h5>
                                    {loadingSalesRhythm ? (
                                      <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                      </div>
                                    ) : salesRhythm ? (
                                      // Show stock data if we have NF-e entry OR if we have sales data
                                      (salesRhythm.has_nfe_entry || salesRhythm.total_sold > 0 || salesRhythm.sales_last_7_days > 0) ? (
                                        <div className="space-y-3">
                                          <div className="grid grid-cols-3 gap-2 text-xs">
                                            <div className="p-2 rounded-lg bg-blue-50 text-center">
                                              <p className="text-slate-500">Entrada</p>
                                              <p className="font-bold text-blue-700 text-lg">
                                                {salesRhythm.initial_stock > 0 ? salesRhythm.initial_stock : (salesRhythm.total_sold || 0)}
                                              </p>
                                              <p className="text-[10px] text-slate-400">
                                                {salesRhythm.has_nfe_entry ? 'via NF-e' : 'estimado'}
                                              </p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-amber-50 text-center">
                                              <p className="text-slate-500">Vendido</p>
                                              <p className="font-bold text-amber-700 text-lg">
                                                {salesRhythm.total_sold || 0}
                                              </p>
                                              <p className="text-[10px] text-slate-400">unidades</p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-emerald-50 text-center">
                                              <p className="text-slate-500">Restante</p>
                                              <div className="flex justify-center">
                                                <StockDualValue
                                                  product={{ ...(stockProductDetail?.product || {}), current_stock: salesRhythm.current_stock || 0 }}
                                                  size="sm"
                                                />
                                              </div>
                                              <p className="text-[10px] text-slate-400">unidades</p>
                                            </div>
                                          </div>
                                          
                                          {/* Progress bar showing stock consumption */}
                                          {(salesRhythm.initial_stock > 0 || salesRhythm.total_sold > 0) && (
                                            <div className="space-y-1">
                                              <div className="flex justify-between text-[10px] text-slate-500">
                                                <span>Consumo do estoque</span>
                                                <span>
                                                  {salesRhythm.initial_stock > 0 
                                                    ? ((salesRhythm.total_sold / salesRhythm.initial_stock) * 100).toFixed(0)
                                                    : 100}%
                                                </span>
                                              </div>
                                              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div 
                                                  className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
                                                  style={{ 
                                                    width: salesRhythm.initial_stock > 0 
                                                      ? `${Math.min(100, (salesRhythm.total_sold / salesRhythm.initial_stock) * 100)}%`
                                                      : '100%'
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Days of stock info */}
                                          {salesRhythm.days_of_stock !== null && salesRhythm.days_of_stock !== undefined && (
                                            <div className="p-2 rounded-lg bg-slate-50 text-center">
                                              <p className="text-xs text-slate-500">Duração estimada do estoque</p>
                                              <p className={`font-bold text-lg ${
                                                salesRhythm.days_of_stock <= 3 
                                                  ? 'text-red-600' 
                                                  : salesRhythm.days_of_stock <= 7 
                                                    ? 'text-amber-600' 
                                                    : 'text-emerald-600'
                                              }`}>
                                                {salesRhythm.days_of_stock} dias
                                              </p>
                                              <p className="text-[10px] text-slate-400">baseado no ritmo de vendas</p>
                                            </div>
                                          )}
                                          
                                          {/* Warning if no NF-e but has sales */}
                                          {!salesRhythm.has_nfe_entry && salesRhythm.total_sold > 0 && (
                                            <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-center">
                                              <p className="text-[10px] text-amber-700">
                                                ⚠️ Sem NF-e - dados baseados em vendas registradas
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 text-center">
                                          <AlertTriangle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                          <p className="text-sm font-medium text-slate-600">Estoque Indisponível</p>
                                          <p className="text-xs text-slate-500 mt-1">
                                            Sem entrada de NF-e ou vendas registradas
                                          </p>
                                          <p className="text-[10px] text-slate-400 mt-2">
                                            Importe uma NF-e para controlar o estoque deste produto
                                          </p>
                                        </div>
                                      )
                                    ) : (
                                      <p className="text-sm text-slate-400 text-center py-2">Sem dados de estoque</p>
                                    )}
                                  </div>

                                  {/* Sales Rhythm Analysis */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100 col-span-full lg:col-span-1">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <TrendingUp className="w-4 h-4" />
                                      Análise de Vendas
                                    </h5>
                                    {loadingSalesRhythm ? (
                                      <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                      </div>
                                    ) : salesRhythm ? (
                                      <div className="space-y-3">
                                        {/* Alert Status */}
                                        {salesRhythm.alert_status?.needs_alert && (
                                          <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                                            <p className="text-xs font-medium text-red-700 flex items-center gap-1">
                                              <AlertTriangle className="w-3 h-3" />
                                              {salesRhythm.alert_status.message}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Target Info */}
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          <div className="p-2 rounded-lg bg-purple-50">
                                            <p className="text-slate-500">Meta diária</p>
                                            <p className="font-bold text-purple-700">
                                              {salesRhythm.target_analysis?.required_daily_sales || 0} un/dia
                                            </p>
                                            <p className="text-[10px] text-slate-400">
                                              p/ vender em {salesRhythm.target_analysis?.sell_through_days || 7} dias
                                            </p>
                                          </div>
                                          {salesRhythm.expiry_analysis?.earliest_expiry && (
                                            <div className="p-2 rounded-lg bg-amber-50">
                                              <p className="text-slate-500">Meta p/ vencimento</p>
                                              <p className="font-bold text-amber-700">
                                                {salesRhythm.expiry_analysis?.required_daily_sales || 0} un/dia
                                              </p>
                                              <p className="text-[10px] text-slate-400">
                                                vence em {salesRhythm.expiry_analysis?.days_until_expiry} dias
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Sales Performance */}
                                        <div className="p-2 rounded-lg bg-slate-50">
                                          <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs text-slate-500">Média atual</p>
                                            <p className={`text-sm font-bold ${
                                              salesRhythm.sales_performance?.average_daily_sales >= 
                                              Math.max(salesRhythm.target_analysis?.required_daily_sales || 0, salesRhythm.expiry_analysis?.required_daily_sales || 0)
                                                ? 'text-emerald-600' 
                                                : 'text-red-600'
                                            }`}>
                                              {salesRhythm.sales_performance?.average_daily_sales || 0} un/dia
                                            </p>
                                          </div>
                                          
                                          {/* Daily Breakdown */}
                                          <div className="flex gap-1 overflow-x-auto pb-1">
                                            {salesRhythm.sales_performance?.breakdown?.map((day, idx) => {
                                              const requiredDaily = Math.max(
                                                salesRhythm.target_analysis?.required_daily_sales || 0, 
                                                salesRhythm.expiry_analysis?.required_daily_sales || 0
                                              );
                                              const isBelowTarget = day.quantity < requiredDaily;
                                              return (
                                                <div 
                                                  key={idx}
                                                  className={`flex-shrink-0 p-1.5 rounded text-center min-w-[40px] ${
                                                    day.is_today 
                                                      ? 'bg-blue-100 border border-blue-300'
                                                      : isBelowTarget 
                                                        ? 'bg-red-50 border border-red-200' 
                                                        : 'bg-emerald-50 border border-emerald-200'
                                                  }`}
                                                >
                                                  <p className="text-[10px] text-slate-500">{day.weekday}</p>
                                                  <p className={`text-xs font-bold ${
                                                    day.is_today 
                                                      ? 'text-blue-700'
                                                      : isBelowTarget ? 'text-red-600' : 'text-emerald-600'
                                                  }`}>
                                                    {day.quantity}
                                                  </p>
                                                  <p className="text-[9px] text-slate-400">{day.date_display}</p>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-slate-400 text-center py-2">Sem dados de vendas</p>
                                    )}
                                  </div>

                                  {/* Batches (Lotes) - Editable */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <Calendar className="w-4 h-4" />
                                      Lotes Ativos ({stockProductDetail.batches?.length || 0})
                                    </h5>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {stockProductDetail.batches?.map((batch, i) => (
                                        <div key={batch.id || i} className="p-2 bg-slate-50 rounded-lg text-sm hover:bg-slate-100 transition-colors">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium">Lote {batch.batch_number}</span>
                                              {batch.stock_type && (
                                                <Badge className={`text-[10px] px-1.5 py-0 ${
                                                  batch.stock_type === 'fifo' 
                                                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                                                    : 'bg-slate-100 text-slate-600 border-slate-300'
                                                }`}>
                                                  {batch.stock_type === 'fifo' ? 'FIFO' : 'Regular'}
                                                </Badge>
                                              )}
                                            </div>
                                            <span className="font-semibold text-emerald-600">{batch.remaining_qty} un</span>
                                          </div>
                                          <div className="flex items-center justify-between mt-1">
                                            {editingBatchExpiry === batch.id ? (
                                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <Input
                                                  type="date"
                                                  value={newBatchExpiry}
                                                  onChange={(e) => setNewBatchExpiry(e.target.value)}
                                                  className="h-6 w-32 text-xs"
                                                />
                                                <Button 
                                                  size="sm" 
                                                  className="h-6 px-1.5" 
                                                  onClick={() => handleUpdateBatchExpiry(batch.id, newBatchExpiry)}
                                                >
                                                  <Check className="w-3 h-3" />
                                                </Button>
                                                <Button 
                                                  size="sm" 
                                                  variant="ghost"
                                                  className="h-6 px-1.5" 
                                                  onClick={() => setEditingBatchExpiry(null)}
                                                >
                                                  <X className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            ) : (
                                              <button
                                                onClick={(e) => { 
                                                  e.stopPropagation(); 
                                                  setEditingBatchExpiry(batch.id);
                                                  setNewBatchExpiry(batch.expiry_date || '');
                                                }}
                                                className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors"
                                                title="Clique para editar validade"
                                              >
                                                <Calendar className="w-3 h-3" />
                                                Val: {batch.expiry_date || 'N/A'}
                                                <Edit2 className="w-3 h-3 ml-1 opacity-50" />
                                              </button>
                                            )}
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-slate-400">{batch.store_name}</span>
                                              {/* Delete batch button */}
                                              {confirmDeleteBatch?.id === batch.id ? (
                                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                  <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    className="h-5 px-2 text-[10px]"
                                                    onClick={() => handleDeleteBatch(batch)}
                                                    disabled={deletingBatch === batch.id}
                                                  >
                                                    {deletingBatch === batch.id ? (
                                                      <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                      "Confirmar"
                                                    )}
                                                  </Button>
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-5 px-1"
                                                    onClick={() => setConfirmDeleteBatch(null)}
                                                  >
                                                    <X className="w-3 h-3" />
                                                  </Button>
                                                </div>
                                              ) : (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDeleteBatch(batch);
                                                  }}
                                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                                  title="Excluir lote"
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      {(!stockProductDetail.batches || stockProductDetail.batches.length === 0) && (
                                        <p className="text-sm text-slate-400 text-center py-2">Nenhum lote</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Recent Movements */}
                                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <TrendingUp className="w-4 h-4" />
                                      Movimentações
                                    </h5>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {/* First show all entries (NF-e) */}
                                      {stockProductDetail.movements
                                        ?.filter(m => m.movement_type === 'entrada')
                                        .map((mov, i) => (
                                        <div key={mov.id || `entrada-${i}`} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg text-sm group hover:bg-emerald-100">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-emerald-600">
                                                +{mov.quantity}
                                              </span>
                                              <span className="text-slate-400 text-xs">{mov.created_at?.slice(0, 10)}</span>
                                              <Badge className="text-[9px] px-1 py-0 bg-emerald-100 text-emerald-700">
                                                ENTRADA
                                              </Badge>
                                            </div>
                                            <span className="text-xs text-slate-500 truncate block">{mov.reference}</span>
                                          </div>
                                          
                                          {mov.id && (
                                            <div className="flex items-center gap-1 ml-2">
                                              {confirmDelete === mov.id ? (
                                                <>
                                                  <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteMovement(mov.id); }}
                                                    disabled={deletingMovement === mov.id}
                                                    className="h-6 px-1 text-red-600 hover:text-red-700 hover:bg-red-100"
                                                  >
                                                    {deletingMovement === mov.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                  </Button>
                                                  <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                                                    className="h-6 px-1 text-slate-500"
                                                  >
                                                    <X className="w-3 h-3" />
                                                  </Button>
                                                </>
                                              ) : (
                                                <Button 
                                                  size="sm" 
                                                  variant="ghost"
                                                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(mov.id); }}
                                                  className="h-6 px-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                                                  title="Excluir movimentação"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      
                                      {/* Then show exits (sales) - filter out adjustments/estornos */}
                                      {stockProductDetail.movements
                                        ?.filter(m => m.movement_type === 'saida' && !m.reference?.includes('Estorno'))
                                        .slice(0, 10)
                                        .map((mov, i) => (
                                        <div key={mov.id || `saida-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm group hover:bg-slate-100">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-red-600">
                                                -{mov.quantity}
                                              </span>
                                              <span className="text-slate-400 text-xs">{mov.created_at?.slice(0, 10)}</span>
                                            </div>
                                            <span className="text-xs text-slate-500 truncate block">{mov.reference}</span>
                                          </div>
                                          
                                          {mov.id && (
                                            <div className="flex items-center gap-1 ml-2">
                                              {confirmDelete === mov.id ? (
                                                <>
                                                  <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteMovement(mov.id); }}
                                                    disabled={deletingMovement === mov.id}
                                                    className="h-6 px-1 text-red-600 hover:text-red-700 hover:bg-red-100"
                                                  >
                                                    {deletingMovement === mov.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                  </Button>
                                                  <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                                                    className="h-6 px-1 text-slate-500"
                                                  >
                                                    <X className="w-3 h-3" />
                                                  </Button>
                                                </>
                                              ) : (
                                                <Button 
                                                  size="sm" 
                                                  variant="ghost"
                                                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(mov.id); }}
                                                  className="h-6 px-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                                                  title="Excluir"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      
                                      {/* Show adjustments/reversals (estornos) */}
                                      {stockProductDetail.movements
                                        ?.filter(m => m.movement_type === 'ajuste' || m.reference?.includes('Estorno'))
                                        .map((mov, i) => (
                                        <div key={mov.id || `ajuste-${i}`} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-sm">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-amber-600">
                                                ↺ {mov.quantity}
                                              </span>
                                              <span className="text-slate-400 text-xs">{mov.created_at?.slice(0, 10)}</span>
                                              <Badge className="text-[9px] px-1 py-0 bg-amber-100 text-amber-700">
                                                ESTORNO
                                              </Badge>
                                            </div>
                                            <span className="text-xs text-slate-500 truncate block">{mov.reference}</span>
                                          </div>
                                        </div>
                                      ))}
                                      
                                      {(!stockProductDetail.movements || stockProductDetail.movements.length === 0) && (
                                        <p className="text-sm text-slate-400 text-center py-2">Nenhuma movimentação</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Price History Card */}
                                  <div className="bg-white rounded-xl p-4 border border-purple-100">
                                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                      <DollarSign className="w-4 h-4 text-purple-600" />
                                      Preços Praticados
                                    </h5>
                                    
                                    {/* Period Filter */}
                                    <div className="flex gap-1 mb-3">
                                      {[
                                        { value: "all", label: "Todos" },
                                        { value: "week", label: "Semana" },
                                        { value: "month", label: "Mês" }
                                      ].map(opt => (
                                        <button
                                          key={opt.value}
                                          onClick={() => setPriceHistoryPeriod(opt.value)}
                                          className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                            priceHistoryPeriod === opt.value
                                              ? 'bg-purple-600 text-white'
                                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                          }`}
                                        >
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>

                                    {loadingPriceHistory ? (
                                      <div className="flex justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                                      </div>
                                    ) : priceHistory?.prices?.length > 0 ? (
                                      <>
                                        {/* Stats Summary */}
                                        <div className="grid grid-cols-3 gap-2 mb-3 p-2 bg-purple-50 rounded-lg">
                                          <div className="text-center">
                                            <p className="text-[10px] text-slate-500">Mínimo</p>
                                            <p className="text-sm font-semibold text-emerald-600">
                                              R$ {priceHistory.stats?.min_price?.toFixed(2)}
                                            </p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-[10px] text-slate-500">Médio</p>
                                            <p className="text-sm font-semibold text-purple-600">
                                              R$ {priceHistory.stats?.avg_price?.toFixed(2)}
                                            </p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-[10px] text-slate-500">Máximo</p>
                                            <p className="text-sm font-semibold text-red-600">
                                              R$ {priceHistory.stats?.max_price?.toFixed(2)}
                                            </p>
                                          </div>
                                        </div>

                                        {/* Price variation indicator */}
                                        {priceHistory.stats?.price_variation > 0 && (
                                          <div className="mb-3 text-center">
                                            <span className="text-xs text-slate-500">Variação: </span>
                                            <span className={`text-xs font-medium ${
                                              priceHistory.stats.price_variation > 50 ? 'text-red-600' : 'text-amber-600'
                                            }`}>
                                              {priceHistory.stats.price_variation.toFixed(1)}%
                                            </span>
                                          </div>
                                        )}

                                        {/* Price List */}
                                        <div className="space-y-1 max-h-32 overflow-y-auto">
                                          {priceHistory.prices.map((p, i) => (
                                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                                              <div className="flex items-center gap-2">
                                                <span className="font-medium text-slate-700">
                                                  R$ {p.price.toFixed(2)}
                                                </span>
                                                {i === 0 && (
                                                  <Badge className="text-[9px] px-1 py-0 bg-purple-100 text-purple-700">
                                                    Mais comum
                                                  </Badge>
                                                )}
                                              </div>
                                              <div className="text-right">
                                                <span className="text-xs text-slate-600">{p.count} vendas</span>
                                                <span className="text-xs text-slate-400 ml-1">({p.total_qty} un)</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        {/* Total summary */}
                                        <div className="mt-2 pt-2 border-t border-slate-100 text-center">
                                          <span className="text-xs text-slate-500">
                                            {priceHistory.stats?.total_sales} vendas • {priceHistory.stats?.unique_prices} preços diferentes
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <p className="text-sm text-slate-400 text-center py-4">
                                        Nenhuma venda no período
                                      </p>
                                    )}
                                  </div>

                                  {/* Name History Card */}
                                  {productNameHistory.length > 0 && (
                                    <div className="bg-white rounded-xl p-4 border border-amber-100 lg:col-span-4">
                                      <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                        <History className="w-4 h-4 text-amber-600" />
                                        Histórico de Alterações de Nome
                                      </h5>
                                      <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {productNameHistory.map((change, i) => (
                                          <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-sm">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-slate-500 line-through text-xs">{change.old_name}</span>
                                                <span className="text-amber-600">→</span>
                                                <span className="font-medium text-slate-700">{change.new_name}</span>
                                              </div>
                                              {change.reason && (
                                                <span className="text-xs text-slate-400 italic">Motivo: {change.reason}</span>
                                              )}
                                            </div>
                                            <span className="text-xs text-slate-400 shrink-0 ml-2">
                                              {new Date(change.changed_at).toLocaleDateString('pt-BR')}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-slate-500 text-center py-4">Nenhum detalhe disponível</p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* ==================== WRITE-OFF TAB ==================== */}
      {activeTab === "writeoff" && (
        <div className="space-y-4">
          {/* Header with Add button */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <select
                value={stockStoreId}
                onChange={(e) => setStockStoreId(e.target.value)}
                className="h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
              >
                <option value="">Todas as Lojas</option>
                {STORE_LOCATIONS.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
              <Button onClick={loadWriteOffs} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              onClick={() => {
                if (!stockStoreId) {
                  toast.error("Selecione uma loja primeiro");
                  return;
                }
                setShowWriteOffModal(true);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Registrar Baixa
            </Button>
          </div>

          {/* Write-off Modal */}
          {showWriteOffModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Registrar Baixa</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowWriteOffModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Product Search */}
                  <div>
                    <Label className="text-sm text-slate-700">EAN do Produto *</Label>
                    <Input
                      value={writeOffEan}
                      onChange={(e) => {
                        setWriteOffEan(e.target.value);
                        searchProductForWriteOff(e.target.value);
                      }}
                      placeholder="Digite o EAN"
                      className="mt-1"
                    />
                    {writeOffProduct && (
                      <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-800">{writeOffProduct.name}</p>
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <span>Estoque:</span>
                          <StockDualValue product={writeOffProduct} size="sm" inline />
                        </div>
                      </div>
                    )}
                    {searchingProduct && !writeOffProduct && (
                      <div className="mt-2 p-3 bg-slate-50 rounded-lg flex items-center gap-2 text-slate-600" data-testid="writeoff-search-loading">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Buscando produto...</span>
                      </div>
                    )}
                    {!searchingProduct && writeOffEan && writeOffEan.length >= 8 && !writeOffProduct && (
                      <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-700 mb-2">
                          ⚠️ Produto não cadastrado. Informe o nome para registrar a baixa:
                        </p>
                        <Input
                          value={writeOffProductName}
                          onChange={(e) => setWriteOffProductName(e.target.value)}
                          placeholder="Nome do produto"
                          className="bg-white"
                        />
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <Label className="text-sm text-slate-700">Quantidade *</Label>
                    <Input
                      type="number"
                      value={writeOffQty}
                      onChange={(e) => setWriteOffQty(e.target.value)}
                      placeholder="0"
                      min="0.01"
                      step="0.01"
                      className="mt-1"
                    />
                  </div>

                  {/* Reason */}
                  <div>
                    <Label className="text-sm text-slate-700">Motivo *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {WRITE_OFF_REASONS.map(reason => (
                        <button
                          key={reason.value}
                          onClick={() => setWriteOffReason(reason.value)}
                          className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                            writeOffReason === reason.value
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {reason.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Collaborator */}
                  <div>
                    <Label className="text-sm text-slate-700">Colaborador *</Label>
                    {!showAddCollaborator ? (
                      <div className="flex gap-2 mt-1">
                        <select
                          value={writeOffCollaborator}
                          onChange={(e) => setWriteOffCollaborator(e.target.value)}
                          className="flex-1 h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                        >
                          <option value="">Selecione...</option>
                          {collaborators.map(c => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowAddCollaborator(true)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={newCollaboratorName}
                          onChange={(e) => setNewCollaboratorName(e.target.value)}
                          placeholder="Nome do colaborador"
                          className="flex-1"
                        />
                        <Button variant="outline" size="sm" onClick={addCollaborator}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowAddCollaborator(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <Label className="text-sm text-slate-700">Observações</Label>
                    <Textarea
                      value={writeOffNotes}
                      onChange={(e) => setWriteOffNotes(e.target.value)}
                      placeholder="Detalhes adicionais..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  {/* Photo */}
                  <div>
                    <Label className="text-sm text-slate-700">Foto</Label>
                    <div className="flex gap-2 mt-2">
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoCapture}
                        className="hidden"
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoCapture}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        onClick={() => cameraInputRef.current?.click()}
                        className="flex-1"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Tirar Foto
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => photoInputRef.current?.click()}
                        className="flex-1"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Galeria
                      </Button>
                    </div>
                    {writeOffPhoto && (
                      <div className="mt-2 relative">
                        <img 
                          src={writeOffPhoto} 
                          alt="Preview" 
                          className="w-full h-40 object-cover rounded-lg"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setWriteOffPhoto(null)}
                          className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={submitWriteOff}
                    disabled={submittingWriteOff || !writeOffEan || !writeOffQty || !writeOffReason || !writeOffCollaborator}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6"
                  >
                    {submittingWriteOff ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrando...</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" />Registrar Baixa</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Write-offs List */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-2 px-3 md:px-6">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" />
                Histórico de Baixas
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 md:px-6">
              {loadingWriteOffs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : writeOffs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Trash2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Nenhuma baixa registrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {writeOffs.map(wo => {
                    const reasonInfo = WRITE_OFF_REASONS.find(r => r.value === wo.reason);
                    return (
                      <div 
                        key={wo.id} 
                        className="p-3 md:p-4 bg-slate-50 rounded-xl border border-slate-200"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-800 truncate">{wo.product_name || wo.ean}</p>
                              <Badge className={`${reasonInfo?.color || 'bg-slate-100 text-slate-700'} text-[10px]`}>
                                {wo.reason_label || wo.reason}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                              <span>EAN: {wo.ean}</span>
                              <span>Qtd: {wo.quantity} {wo.unit}</span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {wo.collaborator_name}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(wo.timestamp).toLocaleString('pt-BR')}
                            </p>
                            {wo.notes && (
                              <p className="text-xs text-slate-500 mt-1 italic">"{wo.notes}"</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 self-end sm:self-start">
                            {wo.photo_base64 && (
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200">
                                <img 
                                  src={wo.photo_base64} 
                                  alt="Foto" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteWriteOff(wo.id)}
                              className="text-slate-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default StockPage;
